import { useState, useEffect, useCallback } from "react";
import type { CustomColumn, DashboardColumnSetting } from "../types/types";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

// 固定列の定義（タイトルは常に先頭固定のため除外）
const FIXED_COLUMNS: { key: string; label: string }[] = [
  { key: "status", label: "ステータス" },
  { key: "assignee", label: "担当者" },
  { key: "endDate", label: "期限" },
  { key: "priority", label: "重要度" },
  { key: "tags", label: "タグ" },
];

interface ColumnItem {
  columnKey: string;
  label: string;
  isVisible: boolean;
  isFixed: boolean;
}

interface Props {
  listId: string;
  honbuId: number;
  roomId: number;
  customColumns: CustomColumn[];
  color: PaletteColor;
  isDarkMode: boolean;
  onClose: () => void;
}

function buildDefaultItems(customColumns: CustomColumn[], roomId: number): ColumnItem[] {
  const fixedItems = FIXED_COLUMNS
    .filter((c) => c.key !== "assignee" || roomId !== 0)
    .map((c) => ({ columnKey: c.key, label: c.label, isVisible: true, isFixed: false }));
  const customItems = customColumns.map((c) => ({
    columnKey: `custom_${c.customColumnId}`,
    label: c.columnName,
    isVisible: true,
    isFixed: false,
  }));
  return [...fixedItems, ...customItems];
}

function settingsToItems(
  settings: DashboardColumnSetting[],
  customColumns: CustomColumn[],
  roomId: number
): ColumnItem[] {
  const allKeys = [
    ...FIXED_COLUMNS.filter((c) => c.key !== "assignee" || roomId !== 0).map((c) => c.key),
    ...customColumns.map((c) => `custom_${c.customColumnId}`),
  ];
  const labelMap: Record<string, string> = {};
  FIXED_COLUMNS.forEach((c) => { labelMap[c.key] = c.label; });
  customColumns.forEach((c) => { labelMap[`custom_${c.customColumnId}`] = c.columnName; });

  // 設定済みの列を順番通りに並べる
  const saved = settings
    .filter((s) => allKeys.includes(s.columnKey))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((s) => ({
      columnKey: s.columnKey,
      label: labelMap[s.columnKey] ?? s.columnKey,
      isVisible: s.isVisible,
      isFixed: false,
    }));

  // 設定に含まれていない列を末尾に追加（新規カスタム列など）
  const savedKeys = new Set(saved.map((s) => s.columnKey));
  const unsaved = allKeys
    .filter((k) => !savedKeys.has(k))
    .map((k) => ({ columnKey: k, label: labelMap[k] ?? k, isVisible: true, isFixed: false }));

  return [...saved, ...unsaved];
}

function itemsToSettings(items: ColumnItem[]): DashboardColumnSetting[] {
  return items.map((item, i) => ({
    columnKey: item.columnKey,
    isVisible: item.isVisible,
    displayOrder: i,
  }));
}

export const DashboardColumnSettingsDialog = ({
  listId,
  honbuId,
  roomId,
  customColumns,
  color,
  isDarkMode,
  onClose,
}: Props) => {
  const accentColor = PaletteColorHex[color];
  const [activeTab, setActiveTab] = useState<"common" | "personal">("common");
  const [commonItems, setCommonItems] = useState<ColumnItem[]>([]);
  const [personalItems, setPersonalItems] = useState<ColumnItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headerStyle = {
    background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
  };
  // ダークモードでは線・文字が沈みやすいため明るめの色を使う（DashboardView と合わせる）
  const bgStyle = isDarkMode
    ? { backgroundColor: "#1e1e1e", color: "#f5f5f5" }
    : { backgroundColor: "#ffffff", color: "#212121" };
  const borderColor = isDarkMode ? "#5a5a5a" : "#e5e7eb";
  const textSecondary = isDarkMode ? "#c0c0c0" : "#757575";

  const loadSettings = useCallback(
    async (isCommon: boolean): Promise<ColumnItem[]> => {
      try {
        const res = await fetch(
          `/api/todolists/${listId}/dashboard-columns?honbuId=${honbuId}&isCommon=${isCommon}`
        );
        if (!res.ok) return buildDefaultItems(customColumns, roomId);
        const data: DashboardColumnSetting[] = await res.json();
        if (data.length === 0) return buildDefaultItems(customColumns, roomId);
        return settingsToItems(data, customColumns, roomId);
      } catch {
        return buildDefaultItems(customColumns, roomId);
      }
    },
    [listId, honbuId, customColumns, roomId]
  );

  useEffect(() => {
    Promise.all([loadSettings(true), loadSettings(false)]).then(
      ([common, personal]) => {
        setCommonItems(common);
        setPersonalItems(personal);
      }
    );
  }, [loadSettings]);

  const items = activeTab === "common" ? commonItems : personalItems;
  const setItems = activeTab === "common" ? setCommonItems : setPersonalItems;

  const moveUp = (index: number) => {
    if (index === 0) return;
    setItems((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index: number) => {
    setItems((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const toggleVisible = (index: number) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, isVisible: !item.isVisible } : item
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const isCommon = activeTab === "common";
    const payload = itemsToSettings(items);
    try {
      const res = await fetch(
        `/api/todolists/${listId}/dashboard-columns?honbuId=${honbuId}&isCommon=${isCommon}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error();
      onClose();
    } catch {
      setError("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative rounded-lg shadow-xl w-full max-w-sm mx-4 flex flex-col overflow-hidden"
        style={{
          ...bgStyle,
          border: isDarkMode ? "1px solid #000" : "1px solid #fff",
          maxHeight: "80vh",
        }}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between px-4 py-3" style={headerStyle}>
          <span className="font-semibold text-white text-sm">列の表示設定</span>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* タブ */}
        <div
          className="flex border-b"
          style={{ borderColor }}
        >
          <button
            onClick={() => setActiveTab("common")}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors"
            style={
              activeTab === "common"
                ? { color: accentColor, borderBottom: `2px solid ${accentColor}` }
                : { color: textSecondary }
            }
          >
            <PeopleOutlineIcon style={{ fontSize: 15 }} />
            共通設定
          </button>
          <button
            onClick={() => setActiveTab("personal")}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors"
            style={
              activeTab === "personal"
                ? { color: accentColor, borderBottom: `2px solid ${accentColor}` }
                : { color: textSecondary }
            }
          >
            <PersonOutlineIcon style={{ fontSize: 15 }} />
            個人設定
          </button>
        </div>

        {/* 本文 */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs mb-3" style={{ color: textSecondary }}>
            {activeTab === "common"
              ? "全メンバーが共有する列の表示・順番を設定します。"
              : "自分だけに適用される列の表示・順番を設定します。"}
          </p>

          {error && (
            <div className="text-red-500 text-xs px-2 py-1 rounded bg-red-50 mb-2">
              {error}
            </div>
          )}

          {/* タイトル（固定） */}
          <div
            className="flex items-center justify-between px-3 py-2 rounded-lg mb-1"
            style={{ background: isDarkMode ? "#2a2a2a" : "#f9fafb", border: `1px solid ${borderColor}` }}
          >
            <div className="flex items-center gap-2">
              <input type="checkbox" checked disabled className="opacity-40 cursor-not-allowed" />
              <span className="text-sm">タスクタイトル</span>
            </div>
            <span className="text-xs" style={{ color: textSecondary }}>
              常に先頭・固定
            </span>
          </div>

          {/* 設定可能な列 */}
          {items.map((item, index) => (
            <div
              key={item.columnKey}
              className="flex items-center justify-between px-3 py-2 rounded-lg mb-1"
              style={{ border: `1px solid ${borderColor}` }}
            >
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={item.isVisible}
                  onChange={() => toggleVisible(index)}
                  className="cursor-pointer"
                />
                <span
                  className="text-sm"
                  style={{ color: item.isVisible ? undefined : textSecondary }}
                >
                  {item.label}
                </span>
                {item.columnKey.startsWith("custom_") && (
                  <span className="text-xs px-1 rounded" style={{ background: accentColor + "20", color: accentColor }}>
                    カスタム
                  </span>
                )}
              </label>
              <div className="flex gap-0.5">
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-20"
                  style={{ color: textSecondary }}
                >
                  <ArrowUpwardIcon style={{ fontSize: 14 }} />
                </button>
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === items.length - 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-20"
                  style={{ color: textSecondary }}
                >
                  <ArrowDownwardIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* フッター */}
        <div
          className="flex items-center justify-end gap-2 px-4 py-3 border-t"
          style={{ borderColor }}
        >
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50 transition-colors"
            style={{ borderColor }}
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm rounded text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: accentColor }}
          >
            {saving ? "保存中..." : "保存する"}
          </button>
        </div>
      </div>
    </div>
  );
};
